/**
 * Cost Summary Component
 *
 * Displays total cost. Optionally shows itemized breakdown.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { CostTotals, Currency } from '../../types';
import {
  DollarSign,
  SquareStack,
  Grid3X3,
  Minus,
  Wrench,
  Receipt,
} from 'lucide-react';
import './CostSummary.css';

interface CostSummaryProps {
  totals: CostTotals;
  currency: Currency;
  showItemized?: boolean;
  onCurrencyChange?: (currency: Currency) => void;
  hideTitle?: boolean;
}

export const CostSummary: React.FC<CostSummaryProps> = ({
  totals,
  currency,
  showItemized = true,
  onCurrencyChange,
  hideTitle = false,
}) => {
  const { t } = useTranslation('results');
  const formatAmount = (usd: number, cad: number): string => {
    const amount = currency === 'USD' ? usd : cad;
    const symbol = currency === 'USD' ? '$' : 'C$';

    return `${symbol}${amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const totalAmount = currency === 'USD' ? totals.totalCostUSD : totals.totalCostCAD;
  // Round up to nearest $100
  const roundedTotal = Math.ceil(totalAmount / 100) * 100;

  return (
    <div className={`cost-summary${hideTitle ? ' cost-summary--no-card' : ''}`}>
      {!hideTitle && (
        <h3 className="cost-summary-title">
          <Receipt size={20} className="cost-title-icon" />
          {t('cost.title')}
        </h3>
      )}

      {showItemized && totals.itemizedCosts && (
        <div className="cost-breakdown">
          <div className="cost-row">
            <span className="cost-label">
              <SquareStack size={16} className="cost-row-icon" />
              {t('bom.sections.wallAndCeilingBoard')}
            </span>
            <span className="cost-value">
              {formatAmount(
                totals.itemizedCosts.wallAndCeilingBoard.usd,
                totals.itemizedCosts.wallAndCeilingBoard.cad
              )}
            </span>
          </div>

          <div className="cost-row">
            <span className="cost-label">
              <Grid3X3 size={16} className="cost-row-icon" />
              {t('cost.slatwall')}
            </span>
            <span className="cost-value">
              {formatAmount(
                totals.itemizedCosts.slatwall.usd,
                totals.itemizedCosts.slatwall.cad
              )}
            </span>
          </div>

          <div className="cost-row">
            <span className="cost-label">
              <Minus size={16} className="cost-row-icon" />
              {t('cost.trims')}
            </span>
            <span className="cost-value">
              {formatAmount(
                totals.itemizedCosts.trims.usd,
                totals.itemizedCosts.trims.cad
              )}
            </span>
          </div>

          {(totals.itemizedCosts.screws.usd > 0 ||
            totals.itemizedCosts.screws.cad > 0) && (
            <div className="cost-row">
              <span className="cost-label">
                <Wrench size={16} className="cost-row-icon" />
                {t('cost.hardware')}
              </span>
              <span className="cost-value">
                {formatAmount(
                  totals.itemizedCosts.screws.usd,
                  totals.itemizedCosts.screws.cad
                )}
              </span>
            </div>
          )}
        </div>
      )}

      <div className="cost-total">
        <span className="cost-total-label">{t('cost.estimatedTotal')}</span>
        <span className="cost-total-value">
          $
          {roundedTotal.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}
        </span>
        <span className="cost-currency-note">{currency}</span>

        {onCurrencyChange && (
          <div className="cost-currency-toggle">
            <span className="currency-toggle-label">
              <DollarSign size={14} />
              {t('cost.displayPriceIn')}
            </span>
            <div className="currency-toggle-buttons">
              <button
                type="button"
                className={`currency-toggle-btn ${currency === 'USD' ? 'active' : ''}`}
                onClick={() => onCurrencyChange('USD')}
              >
                USD ($)
              </button>
              <button
                type="button"
                className={`currency-toggle-btn ${currency === 'CAD' ? 'active' : ''}`}
                onClick={() => onCurrencyChange('CAD')}
              >
                CAD ($)
              </button>
            </div>
          </div>
        )}

        <p className="cost-disclaimer">{t('cost.disclaimer')}</p>
      </div>
    </div>
  );
};

export default CostSummary;
